import { AuthedUser, LabState, LabExampleHttpData, LabExample } from "../reducers/lab";
import { mapObject } from "./map_object";
import { LabHttpState } from "../reducers/lab_http";

// used for both localStorage and sessionStorage
const LAB_DATA_STORAGE_KEY: string = "blockv-devportal-labdata";

/**
 * type definition for lab data stored in sessionStorage
 */
interface LabSessionStorage {
	labState?: LabState;
	labHttpState?: LabHttpState;
}

/**
 * type definition for lab data stored in localStorage
 */
interface LabLocalStorage {
	version?: number;
	authedUser?: AuthedUser;
	authedUsers?: { [id: string]: AuthedUser };
	exampleHistory?: LabExample[];
}

/**
 * passes an instance of the storage to a callback and persists any changes
 * @param callback the callback to execute
 */
function modifyLabSessionStorage(callback: (labStorage: LabSessionStorage) => void) {
	const labStorage: LabSessionStorage =
		JSON.parse(sessionStorage.getItem(LAB_DATA_STORAGE_KEY)) || {};
	callback(labStorage);
	sessionStorage.setItem(LAB_DATA_STORAGE_KEY, JSON.stringify(labStorage));
}

/**
 * hydrates the lab redux state from sessionStorage
 * this approach is useful to persist the state during
 * page refreshes
 * @param state the current lab state
 */
export function hydrateLabStateFromSessionStorage(): LabState {
	const labDataStorage = JSON.parse(sessionStorage.getItem(LAB_DATA_STORAGE_KEY)) || {};
	const labState: LabState = labDataStorage.labState || {};

	return {
		...labState,

		// always exit fullscreen
		isFullscreen: false,

		// if the previous state was fullscreen, reset the splitSize to "50%" (to ensure the split is visible)
		splitSize: !labState.splitSize || labState.isFullscreen ? "50%" : labState.splitSize,

		// always reset the token refresh status
		isRefreshingToken: false,
	};
}

/**
 * hydrates the lab http redux state from sessionStorage
 * this approach is useful to persist the state during
 * page refreshes
 * @param state the current lab http state
 */
export function hydrateLabHttpStateFromSessionStorage(state: LabHttpState): LabHttpState {
	const labDataStorage = JSON.parse(sessionStorage.getItem(LAB_DATA_STORAGE_KEY)) || {};

	const labHttpState = labDataStorage.labHttpState;

	const mergedLabHttpState: LabHttpState = {
		...state,
		...labHttpState,
	};

	return {
		...mergedLabHttpState,
	};
}

/**
 * persist the lab state to sessionStorage
 * @param state the current lab state
 */
export function persistLabToSessionStorage(state: LabState) {
	modifyLabSessionStorage(labStorage => {
		labStorage.labState = state;
	});
}

/**
 * persist the lab state to sessionStorage
 * @param state the current lab state
 */
export function persistLabHttpToSessionStorage(state: LabHttpState) {
	modifyLabSessionStorage(labStorage => {
		labStorage.labHttpState = state;
	});
}

/**
 * passes an instance of the storage to a callback and persists any changes
 * @param callback the callback to execute
 */
function modifyLabLocalStorage(callback: (labStorage: LabLocalStorage) => void) {
	const labStorage = JSON.parse(localStorage.getItem(LAB_DATA_STORAGE_KEY)) || {};
	callback(labStorage);
	localStorage.setItem(LAB_DATA_STORAGE_KEY, JSON.stringify(labStorage));
}

/**
 * hydrates the lab redux state from localStorage
 * this approach is useful to persist the state across
 * browser window close and reopen events
 * @param state the current lab state
 */
export function hydrateLabStateFromLocalStorage(): LabState {
	const labDataStorage = JSON.parse(localStorage.getItem(LAB_DATA_STORAGE_KEY));

	// no data to hydrate from
	if (!labDataStorage) {
		return {} as LabState;
	}

	return {
		authedUser: {
			id: labDataStorage.authedUser.id,
			firstName: labDataStorage.authedUser.firstName,
			lastName: labDataStorage.authedUser.lastName,
			isAdmin: labDataStorage.authedUser.isAdmin,
			accessToken: labDataStorage.authedUser.accessToken,
			refreshToken: labDataStorage.authedUser.refreshToken,
		},
		authedUsers: mapObject(
			labDataStorage.authedUsers,
			(user: any): AuthedUser => ({
				id: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
				isAdmin: user.isAdmin,
				accessToken: user.accessToken,
				refreshToken: user.refreshToken,
			})
		),
		exampleHistory: labDataStorage.exampleHistory.map((example: any) => ({
			key: example.key,
			type: example.type,
			timestampIso: example.timestampIso,
			meta: {
				docTitle: example.meta.docTitle,
				heading: example.meta.heading,
			},
			data: example.data,
		})),
	} as LabState;
}

/**
 * persist some of the lab state to localStorage
 * @param state the current lab state
 */
export function persistLabToLocalStorage(state: LabState) {
	modifyLabLocalStorage(storage => {
		storage.version = storage.version || 1;
		if(state.authedUser) {
			storage.authedUser = {
				id: state.authedUser.id,
				firstName: state.authedUser.firstName,
				lastName: state.authedUser.lastName,
				isAdmin: state.authedUser.isAdmin,
				accessToken: state.authedUser.accessToken,
				refreshToken: state.authedUser.refreshToken,
			};
		}
		storage.authedUsers = mapObject(
			state.authedUsers,
			(user: any): AuthedUser => ({
				id: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
				isAdmin: user.isAdmin,
				accessToken: user.accessToken,
				refreshToken: user.refreshToken,
			})
		);
		storage.exampleHistory = state.exampleHistory.map((example: any) => ({
			key: example.key,
			type: example.type,
			timestampIso: example.timestampIso,
			meta: {
				docTitle: example.meta.docTitle,
				heading: example.meta.heading,
			},
			data: example.data,
		}));
	});
}

/**
 * get an example from history localStorage by key
 * @param key
 */
export function getExampleFromHistory(key: number): LabExample | undefined {
	const labDataStorage = JSON.parse(localStorage.getItem(LAB_DATA_STORAGE_KEY));

	if (!labDataStorage || !Array.isArray(labDataStorage.exampleHistory)) {
		return undefined;
	}

	const example = labDataStorage.exampleHistory.find(e => e.key === key);
	return example;
}

/**
 * persist appropriate sections of the lab redux state into local and session storage
 * @param state the current lab state
 */
export function persistLab(state: LabState) {
	persistLabToSessionStorage(state);
	persistLabToLocalStorage(state);
}

/**
 * remove all persisted data for the lab in localStorage and sessionStorage
 * used when logging out
 */
export function removeAllLabPersistedData() {
	localStorage.removeItem(LAB_DATA_STORAGE_KEY);
	sessionStorage.removeItem(LAB_DATA_STORAGE_KEY);
}



// WEBPACK FOOTER //
// ./src/utils/lab_persist.ts